/**
 * Letter confusable mappings for dictation letter picker.
 *
 * Each letter maps to letters that are commonly confused by
 * ESL learners (especially Asian learners), based on:
 * - Phonetic similarity (b/p, l/r, f/v, d/t, s/z, m/n)
 * - Visual similarity (b/d, p/q, m/n, u/v)
 * - Common spelling mistakes (a/e vowel confusion, i/e)
 */

/** Phonetic + visual confusable map (lowercase) */
const CONFUSABLE_MAP: Record<string, string[]> = {
  // Consonants — phonetic pairs + nearby sounds
  b: ['p', 'd', 'v', 'g', 't'],
  c: ['k', 's', 'g', 'q', 'x'],
  d: ['t', 'b', 'g', 'p', 'th'],
  f: ['v', 'p', 'h', 'th', 's'],
  g: ['k', 'c', 'j', 'd', 'q'],
  h: ['f', 'k', 'n', 'j', 'ch'],
  j: ['g', 'ch', 'y', 'z', 'dg'],
  k: ['c', 'g', 'q', 'x', 'ck'],
  l: ['r', 'n', 'w', 'i', 'y'],
  m: ['n', 'w', 'b', 'p', 'mm'],
  n: ['m', 'l', 'ng', 'r', 'nn'],
  p: ['b', 't', 'f', 'k', 'q'],
  q: ['k', 'g', 'c', 'p', 'qu'],
  r: ['l', 'w', 'n', 'v', 'rr'],
  s: ['z', 'c', 'x', 'sh', 'th'],
  t: ['d', 'p', 'k', 'th', 'tt'],
  v: ['b', 'f', 'w', 'u', 'th'],
  w: ['v', 'r', 'l', 'u', 'wh'],
  x: ['s', 'z', 'k', 'ks', 'c'],
  y: ['i', 'j', 'e', 'w', 'ie'],
  z: ['s', 'x', 'j', 'th', 'zz'],

  // Vowels — ESL learner confusion patterns
  a: ['e', 'u', 'o', 'i', 'ah'],
  e: ['i', 'a', 'u', 'o', 'ea'],
  i: ['e', 'y', 'a', 'u', 'ee'],
  o: ['u', 'a', 'e', 'oo', 'aw'],
  u: ['o', 'v', 'w', 'a', 'oo'],
};

/** All single lowercase letters */
const ALL_LETTERS = 'abcdefghijklmnopqrstuvwxyz'.split('');

/**
 * Generate letter options for a given target letter.
 *
 * @param targetChar - The correct character (will be lowercased)
 * @param totalOptions - Total number of options to show (default: 7)
 * @param prevOptions - Previous round's options array. When provided, any letter
 *                     that appears in both the new option set and the previous
 *                     options is locked to its previous index — this preserves
 *                     finger-memory across consecutive prompts and avoids the
 *                     "I tapped where I expected it to be, but it moved" error.
 * @returns Array of lowercase single-letter options, including the target
 */
export function generateLetterOptions(
  targetChar: string,
  totalOptions = 7,
  prevOptions?: string[],
): string[] {
  const target = targetChar.toLowerCase();

  // If not a letter (digit, special char), return just the target
  if (!/^[a-z]$/.test(target)) {
    return [target];
  }

  const confusables = (CONFUSABLE_MAP[target] || [])
    // Only keep single-letter confusables for the picker buttons
    .filter((c) => c.length === 1 && c !== target);

  const optionSet = new Set<string>([target]);

  // Add confusable letters first (most relevant distractors)
  for (const c of confusables) {
    if (optionSet.size >= totalOptions) break;
    optionSet.add(c);
  }

  // Fill remaining slots with random letters (not already included)
  if (optionSet.size < totalOptions) {
    const remaining = ALL_LETTERS.filter((l) => !optionSet.has(l));
    for (let i = remaining.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [remaining[i], remaining[j]] = [remaining[j], remaining[i]];
    }
    for (const l of remaining) {
      if (optionSet.size >= totalOptions) break;
      optionSet.add(l);
    }
  }

  const letters = Array.from(optionSet);

  // Position-lock pass: keep letters that also appeared in the previous round
  // at their previous indices, so finger memory stays valid across prompts.
  if (prevOptions && prevOptions.length > 0) {
    const slots: (string | null)[] = new Array(totalOptions).fill(null);
    const locked = new Set<string>();
    for (let i = 0; i < totalOptions; i++) {
      const prev = prevOptions[i];
      if (prev && optionSet.has(prev) && !locked.has(prev)) {
        slots[i] = prev;
        locked.add(prev);
      }
    }
    const free = letters.filter((l) => !locked.has(l));
    for (let i = free.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [free[i], free[j]] = [free[j], free[i]];
    }
    let fi = 0;
    for (let i = 0; i < totalOptions; i++) {
      if (slots[i] === null) {
        slots[i] = free[fi++] ?? null;
      }
    }
    return slots.filter((s): s is string => s !== null);
  }

  // No previous options — Fisher-Yates shuffle (first prompt)
  for (let i = letters.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [letters[i], letters[j]] = [letters[j], letters[i]];
  }
  return letters;
}

/**
 * Check if a character should be auto-filled (not require user input).
 * Spaces, punctuation, digits are auto-filled.
 */
export function isAutoFillChar(char: string): boolean {
  return !/^[a-zA-Z]$/.test(char);
}

/**
 * Parse a sentence into a sequence of characters with metadata.
 * Returns an array where each entry is either:
 * - { char, autoFill: false } for letters (user must input)
 * - { char, autoFill: true } for spaces/punctuation (auto-filled)
 */
export interface CharEntry {
  char: string;
  autoFill: boolean;
  index: number;
}

export function parseSentenceForPicker(sentence: string): CharEntry[] {
  return sentence.split('').map((char, index) => ({
    char,
    autoFill: isAutoFillChar(char),
    index,
  }));
}
